import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Calendar,
  Check,
  ChevronDown,
  Clock,
  MapPin,
  Search,
  Trash2,
  User,
  X,
} from "lucide-react";
import { createSchedule, updateSchedule } from "../../api";
import {
  TIME_OPTIONS,
  VISIT_TITLE,
  combineDateAndTime,
  describeScheduleError,
  formatDateKeyLong,
  formatTimeValue12,
  getId,
  toDateKey,
  toTimeValue,
} from "./scheduleUtils";

const DEFAULT_START = "10:00";
const DEFAULT_END = "11:00";

// Searchable list of options shown inline under its field (Balgruha / Coach).
function PickerField({
  id,
  label,
  icon,
  placeholder,
  options,
  selectedIds,
  multiple,
  open,
  onToggleOpen,
  onToggleOption,
  searchPlaceholder,
  emptyText,
}) {
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setSearch("");
  }, [open]);

  const selectedNames = options
    .filter((option) => selectedIds.includes(option.id))
    .map((option) => option.name);
  const summary =
    selectedNames.length === 0
      ? placeholder
      : selectedNames.length > 2
        ? `${selectedNames[0]} +${selectedNames.length - 1} more`
        : selectedNames.join(", ");

  const query = search.trim().toLowerCase();
  const visibleOptions = query
    ? options.filter((option) => option.name.toLowerCase().includes(query))
    : options;

  return (
    <div className="ads-field">
      <span className="ads-field-label" id={`${id}-label`}>
        {label} <span className="ads-required" aria-hidden="true">*</span>
      </span>
      <button
        type="button"
        id={id}
        className={`ads-picker-toggle${open ? " is-open" : ""}`}
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-labelledby={`${id}-label ${id}`}
        onClick={onToggleOpen}
      >
        {icon}
        <span className={`ads-picker-summary${selectedNames.length === 0 ? " is-placeholder" : ""}`}>
          {summary}
        </span>
        <ChevronDown size={16} className="ads-picker-chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="ads-picker-panel" id={`${id}-list`}>
          <div className="ads-picker-search">
            <Search size={14} aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          </div>
          <div
            className="ads-picker-options"
            role={multiple ? "group" : "radiogroup"}
            aria-labelledby={`${id}-label`}
          >
            {visibleOptions.length === 0 && <p className="ads-picker-empty">{emptyText}</p>}
            {visibleOptions.map((option) => {
              const checked = selectedIds.includes(option.id);
              return (
                <label key={option.id} className={`ads-picker-option${checked ? " is-checked" : ""}`}>
                  <input
                    type={multiple ? "checkbox" : "radio"}
                    name={`${id}-option`}
                    checked={checked}
                    onChange={() => onToggleOption(option.id)}
                  />
                  <span className="ads-picker-option-name">{option.name}</span>
                  {checked && <Check size={14} className="ads-picker-option-check" aria-hidden="true" />}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TimeField({ id, label, value, onChange }) {
  const options = TIME_OPTIONS.includes(value) || !value ? TIME_OPTIONS : [...TIME_OPTIONS, value].sort();
  return (
    <div className="ads-field">
      <label className="ads-field-label" htmlFor={id}>
        {label} <span className="ads-required" aria-hidden="true">*</span>
      </label>
      <div className="ads-field-control">
        <Clock size={15} aria-hidden="true" />
        <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => (
            <option key={option} value={option}>
              {formatTimeValue12(option)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default function VisitModal({
  mode,
  visit,
  defaults = {},
  centres,
  coaches,
  onClose,
  onSaved,
  onRequestCancelSchedule,
  isBlocked = false,
}) {
  const isEdit = mode === "edit";
  const dialogRef = useRef(null);
  const originalDateKey = isEdit ? visit?.dateKey : null;

  const [centreIds, setCentreIds] = useState(() =>
    isEdit ? [getId(visit?.balagruhaId)].filter(Boolean) : defaults.centreIds || []
  );
  const [coachId, setCoachId] = useState(() =>
    isEdit ? getId(visit?.assignedTo) : defaults.coachId || ""
  );
  const [dateKey, setDateKey] = useState(() =>
    isEdit ? visit?.dateKey || "" : defaults.dateKey || toDateKey(new Date())
  );
  const [startTime, setStartTime] = useState(() =>
    isEdit ? toTimeValue(visit?.startTime) || DEFAULT_START : DEFAULT_START
  );
  const [endTime, setEndTime] = useState(() =>
    isEdit ? toTimeValue(visit?.endTime) || DEFAULT_END : DEFAULT_END
  );
  const [openPicker, setOpenPicker] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const centreOptions = useMemo(
    () => centres.map((centre) => ({ id: centre._id, name: centre.name, color: centre.color })),
    [centres]
  );

  // A visit may belong to someone who is no longer listed as a coach; keep
  // them selectable so editing does not silently reassign the visit.
  const coachOptions = useMemo(() => {
    const options = coaches.map((coach) => ({ id: coach._id, name: coach.name }));
    const assigneeId = isEdit ? getId(visit?.assignedTo) : "";
    if (assigneeId && !options.some((option) => option.id === assigneeId)) {
      options.unshift({ id: assigneeId, name: visit?.assignedToUser?.name || "Assigned user" });
    }
    return options;
  }, [coaches, isEdit, visit]);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // While a confirmation is open on top, Escape belongs to that dialog.
  useEffect(() => {
    if (isBlocked) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isBlocked, onClose, saving]);

  const toggleCentre = (id) => {
    setError("");
    if (isEdit) {
      // An existing visit belongs to exactly one Balgruha.
      setCentreIds([id]);
      setOpenPicker(null);
      return;
    }
    setCentreIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
    );
  };

  const selectCoach = (id) => {
    setError("");
    setCoachId(id);
    setOpenPicker(null);
  };

  const firstCentre = centreOptions.find((option) => centreIds.includes(option.id));

  const validate = () => {
    if (centreIds.length === 0) return "Please select a Balgruha.";
    if (!coachId) return "Please select a coach.";
    if (!dateKey) return "Please select a date.";
    if (dateKey < toDateKey(new Date()) && dateKey !== originalDateKey) {
      return "Visit date cannot be in the past.";
    }
    if (!startTime || !endTime) return "Please select a start and end time.";
    if (combineDateAndTime(dateKey, startTime) >= combineDateAndTime(dateKey, endTime)) {
      return "End time must be after the start time.";
    }
    return "";
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const start = combineDateAndTime(dateKey, startTime);
    const end = combineDateAndTime(dateKey, endTime);
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await updateSchedule(
          {
            balagruhaId: centreIds[0],
            assignedTo: coachId,
            // Keep the stored date untouched unless the day actually changed.
            date: dateKey === originalDateKey && visit.date ? visit.date : dateKey,
            startTime: start.toISOString(),
            endTime: end.toISOString(),
          },
          visit._id
        );
        onSaved("Visit updated successfully");
      } else {
        await createSchedule({
          assignedTo: [coachId],
          balagruhaIds: centreIds,
          schedules: [
            {
              date: dateKey,
              startTime: start.toISOString(),
              endTime: end.toISOString(),
              title: VISIT_TITLE,
              description: "",
            },
          ],
        });
        onSaved(centreIds.length > 1 ? `${centreIds.length} visits added successfully` : "Visit added successfully");
      }
    } catch (saveError) {
      setError(
        describeScheduleError(saveError, isEdit ? "Failed to update the visit" : "Failed to add the visit")
      );
      setSaving(false);
    }
  };

  const titleId = "ads-visit-modal-title";

  return (
    <div className="ads-modal-backdrop">
      <div
        ref={dialogRef}
        className="ads-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="ads-modal-header">
          <h2 id={titleId}>{isEdit ? "Edit Visit" : "Add Visit"}</h2>
          <button
            type="button"
            className="ads-icon-button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="ads-modal-body">
          <div className="ads-modal-column">
            <PickerField
              id="ads-visit-centre"
              label="Balgruha"
              icon={
                <MapPin
                  size={16}
                  className="ads-picker-icon ads-pin-icon"
                  color={firstCentre?.color || "#2563eb"}
                  fill={firstCentre?.color || "#2563eb"}
                  aria-hidden="true"
                />
              }
              placeholder={isEdit ? "Select Balgruha" : "Select Balgruha(s)"}
              options={centreOptions}
              selectedIds={centreIds}
              multiple={!isEdit}
              open={openPicker === "centre"}
              onToggleOpen={() => setOpenPicker((current) => (current === "centre" ? null : "centre"))}
              onToggleOption={toggleCentre}
              searchPlaceholder="Search Balgruha"
              emptyText="No Balgruha found"
            />
          </div>

          <div className="ads-modal-column">
            <PickerField
              id="ads-visit-coach"
              label="Coach"
              icon={<User size={16} className="ads-picker-icon" aria-hidden="true" />}
              placeholder="Select coach"
              options={coachOptions}
              selectedIds={coachId ? [coachId] : []}
              multiple={false}
              open={openPicker === "coach"}
              onToggleOpen={() => setOpenPicker((current) => (current === "coach" ? null : "coach"))}
              onToggleOption={selectCoach}
              searchPlaceholder="Search Coach"
              emptyText="No coach found"
            />

            <div className="ads-field-row">
              <div className="ads-field">
                <label className="ads-field-label" htmlFor="ads-visit-date">
                  Date <span className="ads-required" aria-hidden="true">*</span>
                </label>
                <div className="ads-field-control ads-date-control">
                  <Calendar size={15} aria-hidden="true" />
                  <span className={dateKey ? "" : "is-placeholder"}>
                    {dateKey ? formatDateKeyLong(dateKey) : "Select date"}
                  </span>
                  <input
                    id="ads-visit-date"
                    type="date"
                    value={dateKey}
                    onChange={(event) => {
                      setError("");
                      setDateKey(event.target.value);
                    }}
                    onClick={(event) => {
                      try {
                        event.currentTarget.showPicker?.();
                      } catch (pickerError) {
                        // showPicker is unsupported or blocked; typing still works.
                      }
                    }}
                  />
                </div>
              </div>
              <TimeField
                id="ads-visit-start"
                label="Start Time"
                value={startTime}
                onChange={(value) => {
                  setError("");
                  setStartTime(value);
                }}
              />
            </div>

            <div className="ads-field-row">
              <TimeField
                id="ads-visit-end"
                label="End Time"
                value={endTime}
                onChange={(value) => {
                  setError("");
                  setEndTime(value);
                }}
              />
            </div>
          </div>
        </div>

        {error && (
          <p className="ads-modal-error" role="alert">
            {error}
          </p>
        )}

        <div className="ads-modal-footer">
          <button type="button" className="ads-button ads-button-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          {isEdit && (
            <button
              type="button"
              className="ads-button ads-button-danger"
              onClick={() => onRequestCancelSchedule(visit)}
              disabled={saving}
            >
              <Trash2 size={16} aria-hidden="true" />
              Cancel Schedule
            </button>
          )}
          <button type="button" className="ads-button ads-button-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
